import { useCallback, useEffect, useMemo, useState } from 'react';
import { Event, EventStatus } from '../types';
import { mockBackend } from '../services/mockBackend';
import EventCard from './EventCard';
import EventDescription from './EventDescription';
import EventDetailsGrid from './EventDetailsGrid';
import StatusBadge from './StatusBadge';

type CreatorEventsPanelProps = {
  userId: string;
  refreshToken: number;
  onEditEvent: (event: Event) => void;
  onActionComplete: () => void;
};

const statusOptions: Array<{ label: string; value: EventStatus | 'all' }> = [
  { label: 'Todos', value: 'all' },
  { label: 'Em revisão', value: 'pending_review' },
  { label: 'Ajustes', value: 'changes_requested' },
  { label: 'Aprovados', value: 'approved' },
  { label: 'Rejeitados', value: 'rejected' },
  { label: 'Incorporados', value: 'merged' },
  { label: 'Cancelados', value: 'cancelled' },
];

const CreatorEventsPanel = ({
  userId,
  refreshToken,
  onEditEvent,
  onActionComplete,
}: CreatorEventsPanelProps) => {
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<EventStatus | 'all'>('all');
  const [events, setEvents] = useState<Event[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    try {
      const items = await mockBackend.events.listCreatorEvents(userId, { query, status });
      setEvents(items);
      setSelectedEvent((current) =>
        current ? items.find((item) => item.id === current.id) || null : current
      );
    } finally {
      setLoading(false);
    }
  }, [query, status, userId]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents, refreshToken]);

  const latestHistoryMessage = useMemo(() => {
    if (!selectedEvent || selectedEvent.history.length === 0) return '';
    return selectedEvent.history[0].message;
  }, [selectedEvent]);

  const cancelSelected = async () => {
    if (!selectedEvent) return;
    await mockBackend.events.cancelEvent(selectedEvent.id, userId, 'Evento cancelado pelo criador.');
    await loadEvents();
    onActionComplete();
  };

  return (
    <div className="d-grid gap-4">
      <div className="card bg-onyx border-0 shadow-lg">
        <div className="card-body p-4">
          <div className="d-flex flex-column flex-lg-row align-items-start align-items-lg-center justify-content-between gap-3 mb-3">
            <div>
              <h5 className="text-white mb-1">Meus eventos</h5>
              <p className="text-white-50 mb-0">
                Acompanhe status, histórico e tome ações rápidas sem sair do portal.
              </p>
            </div>
            <div className="d-flex flex-wrap gap-2">
              <input
                className="form-control input-contrast"
                style={{ width: 260 }}
                placeholder="Buscar por título ou instituição"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
              />
              <select
                className="form-select input-contrast"
                style={{ width: 220 }}
                value={status}
                onChange={(event) => setStatus(event.target.value as EventStatus | 'all')}
              >
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {loading ? (
            <div className="text-white-50">Carregando seus eventos...</div>
          ) : events.length === 0 ? (
            <div className="empty-box">Nenhum evento encontrado com os filtros atuais.</div>
          ) : (
            <div className="row g-3">
              {events.map((event) => (
                <div className="col-xl-6" key={event.id}>
                  <EventCard event={event} onClick={setSelectedEvent} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {selectedEvent && (
        <div className="event-modal-backdrop" onClick={() => setSelectedEvent(null)}>
          <div className="event-modal submission-modal card bg-onyx border-0 shadow-lg" onClick={(event) => event.stopPropagation()}>
            <div className="card-body p-4">
              <div className="d-flex align-items-start justify-content-between gap-3 mb-4">
                <div>
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <StatusBadge kind="event" status={selectedEvent.status} />
                    {selectedEvent.mergedIntoEventId && (
                      <span className="badge bg-dark text-white border border-secondary">
                        Consolidado em {selectedEvent.mergedIntoEventId}
                      </span>
                    )}
                  </div>
                  <h4 className="text-white mb-1">{selectedEvent.title}</h4>
                  <p className="text-white-50 mb-0">{selectedEvent.institution}</p>
                </div>
                <button type="button" className="btn btn-outline-light btn-sm" onClick={() => setSelectedEvent(null)}>
                  Fechar
                </button>
              </div>

              <div className="card bg-dark border-0 mb-4">
                <div className="card-body">
                  <div className="text-white fw-semibold mb-3">Dados preenchidos</div>
                  <EventDetailsGrid event={selectedEvent} showResponsibility />
                </div>
              </div>

              <div className="card bg-dark border-0 mb-4">
                <div className="card-body">
                  <div className="text-white fw-semibold mb-2">Resumo atual</div>
                  <EventDescription
                    description={selectedEvent.description}
                    format={selectedEvent.descriptionFormat}
                  />
                </div>
              </div>

              <div className="card bg-dark border-0 mb-4">
                <div className="card-body">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <span className="text-white fw-semibold">Última movimentação</span>
                    <small className="text-white-50">{latestHistoryMessage ? 'Atualizado' : 'Sem histórico'}</small>
                  </div>
                  <div className="timeline-list">
                    {selectedEvent.history.map((item) => (
                      <div key={item.id} className="timeline-item">
                        <strong className="text-white d-block">{item.message}</strong>
                        <span className="text-white-50 small">
                          {new Date(item.createdAt).toLocaleString('pt-BR')}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="d-flex flex-wrap gap-2">
                {selectedEvent.status !== 'merged' && selectedEvent.status !== 'cancelled' && (
                  <button
                    type="button"
                    className="btn btn-brand"
                    onClick={() => {
                      onEditEvent(selectedEvent);
                      setSelectedEvent(null);
                    }}
                  >
                    Editar no formulário
                  </button>
                )}
                {selectedEvent.status !== 'merged' && selectedEvent.status !== 'cancelled' && (
                  <button type="button" className="btn btn-outline-light" onClick={cancelSelected}>
                    Cancelar evento
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreatorEventsPanel;
